/**
 * In-memory cache of documents already opened in this tab, keyed by the caller's cache key (e.g. the lab
 * order id). Reopening a report is instant and costs no request; the object URL stays valid for the life
 * of the entry instead of being revoked when a dialog closes.
 *
 * Scope and limits, in the spirit of how document viewers handle this:
 *  - memory only (never localStorage/IndexedDB — these are clinical documents);
 *  - bounded (entries expire after TTL_MS and the total is capped at MAX_BYTES, oldest evicted first);
 *  - cleared on sign-out of either session type, and when the page is discarded.
 * The server side pairs with this through ETag/304 on the report endpoints, so even a cache miss after a
 * reload is a revalidation, not a re-download, when the browser still holds the bytes.
 */
export type PdfLoaderResult = { data?: { blob: Blob; fileName?: string }; error?: string };
export type PdfLoader = () => Promise<PdfLoaderResult>;

interface Entry { url: string; fileName: string; bytes: number; lastUsed: number }

const TTL_MS = 15 * 60 * 1000;
const MAX_BYTES = 40 * 1024 * 1024;
const entries = new Map<string, Entry>();
const inFlight = new Map<string, Promise<{ url: string; fileName: string } | { error: string }>>();

function totalBytes(): number {
  let n = 0;
  entries.forEach((e) => { n += e.bytes; });
  return n;
}

function evict(key: string) {
  const e = entries.get(key);
  if (!e) return;
  entries.delete(key);
  try { URL.revokeObjectURL(e.url); } catch { /* already gone */ }
}

function makeRoom(incoming: number) {
  const now = Date.now();
  entries.forEach((e, k) => { if (now - e.lastUsed > TTL_MS) evict(k); });
  while (entries.size > 0 && totalBytes() + incoming > MAX_BYTES) {
    let oldestKey: string | null = null; let oldest = Infinity;
    entries.forEach((e, k) => { if (e.lastUsed < oldest) { oldest = e.lastUsed; oldestKey = k; } });
    if (oldestKey === null) break;
    evict(oldestKey);
  }
}

/** The cached object URL for `key`, or fetch it with `load`, cache it and return it. Concurrent callers share one fetch. */
export function getPdf(key: string, load: PdfLoader): Promise<{ url: string; fileName: string } | { error: string }> {
  const hit = entries.get(key);
  if (hit && Date.now() - hit.lastUsed <= TTL_MS) {
    hit.lastUsed = Date.now();
    return Promise.resolve({ url: hit.url, fileName: hit.fileName });
  }
  if (hit) evict(key);
  const pending = inFlight.get(key);
  if (pending) return pending;

  const p = load()
    .then((res): { url: string; fileName: string } | { error: string } => {
      if (res.error || !res.data) return { error: res.error ?? 'Could not load the document' };
      const blob = new Blob([res.data.blob], { type: 'application/pdf' });
      makeRoom(blob.size);
      const url = URL.createObjectURL(blob);
      entries.set(key, { url, fileName: res.data.fileName || 'document.pdf', bytes: blob.size, lastUsed: Date.now() });
      return { url, fileName: res.data.fileName || 'document.pdf' };
    })
    .catch((e: Error) => ({ error: e.message || 'Could not load the document' }))
    .finally(() => { inFlight.delete(key); });
  inFlight.set(key, p);
  return p;
}

/** Forget one document (e.g. after an amendment produced a new version). */
export function invalidatePdf(key: string) { evict(key); }

/** Forget everything — called on sign-out so the next account never sees a previous account's documents. */
export function clearPdfCache() {
  Array.from(entries.keys()).forEach(evict);
  inFlight.clear();
}

/** Visible for tests. */
export function pdfCacheSize(): number { return entries.size; }

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', clearPdfCache);
}
